<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { addressFields } from "@/util/methods";
import { UICollapse, UICollapseItem } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import AddressAutoComplete from "./AddressAutoComplete.vue";
import AddressChildCardSettings from "./AddressChildCardSettings.vue";
import AddressMandatorySearchBar from "./AddressMandatorySearchBar.vue";
import ChildFieldCheckbox from "./ChildFieldCheckbox.vue";

const store = useAppStore();
const { t } = useI18n();

const props = defineProps({
  address: {
    type: Object,
    required: true,
  },
  selectedIndex: {
    type: Number,
    required: true,
  },
  selectedSlideIndex: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const childrensOrder = ["address", "city", "state", "country", "postal_code"];

// Sort the children based on the order defined in childrensOrder
const sortedChildren = computed(() => {
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  return store.addressSettings.children.sort(
    (a, b) => childrensOrder.indexOf(a.tag) - childrensOrder.indexOf(b.tag)
  );
});
</script>

<template>
  <div class="font-inter text-xs font-medium leading-5 text-gray-700">
    {{ t("common.addressSettings.addressfields") }}
  </div>
  <div class="mb-8 mt-2 flex flex-wrap gap-2">
    <template v-for="element in addressFields" :key="element.tag">
      <ChildFieldCheckbox :element="element" />
    </template>
  </div>
  <UICollapse
    id="custom-field-settings"
    :arrow-placement="'left'"
    :default-expanded-names="['General Settings', 'Auto Complete Address']"
  >
    <UICollapseItem
      v-for="(eachElement, index) in sortedChildren"
      :id="eachElement.tag"
      :key="eachElement.tag"
      :name="eachElement.title"
    >
      <template #header
        ><div class="font-inter flex text-sm font-medium normal-case leading-5 text-gray-700">
          <div>
            {{
              eachElement.tag == "address"
                ? t("common.addressSettings.streetAddress")
                : eachElement.title
            }}
          </div>
        </div></template
      >
      <AddressChildCardSettings :key="eachElement.tag" :index="index" />
    </UICollapseItem>
    <UICollapseItem
      id="address-auto-complete"
      :name="t('common.addressSettings.autoCompleteAddressTitle')"
      class="normal-case"
    >
      <template #header>
        <div class="font-inter flex text-sm font-medium leading-5 text-gray-900">
          <div class="normal-case">{{ t("common.addressSettings.autoCompleteAddressTitle") }}</div>
          <div class="ml-1 flex items-center rounded-full bg-blue-100 px-2.5 text-primary-700">
            <div class="normal-case">{{ t("common.addressSettings.newBadge") }}</div>
          </div>
        </div>
      </template>
      <AddressAutoComplete
        :selected-index="selectedIndex"
        :selected-slide-index="selectedSlideIndex"
      />
      <AddressMandatorySearchBar
        :selected-index="selectedIndex"
        :selected-slide-index="selectedSlideIndex"
      />
    </UICollapseItem>
  </UICollapse>
</template>
